import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, Clock, Package, FileDown } from "lucide-react";
import { api, errMsg, orderKey, downloadBlob } from "@/lib/api";
import { useApp } from "@/context/AppContext";

function loadSnap(config) {
  return new Promise((resolve, reject) => {
    if (window.snap) return resolve(window.snap);
    const s = document.createElement("script");
    s.src = config.midtrans_production ? "https://app.midtrans.com/snap/snap.js" : "https://app.sandbox.midtrans.com/snap/snap.js";
    s.setAttribute("data-client-key", config.midtrans_client_key);
    s.onload = () => resolve(window.snap);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

const STATUS_STYLE = { pending: "bg-[#B3261E]", paid: "bg-[#D9A400] !text-[#1A1412]", processing: "bg-[#D9A400] !text-[#1A1412]", shipped: "bg-[#2E7D32]", ready_for_pickup: "bg-[#2E7D32]", completed: "bg-[#9E9E9E]", cancelled: "bg-[#424242]" };
export const StatusBadge = ({ s }) => <span data-testid="order-status-badge" className={`${STATUS_STYLE[s] || "bg-gray-500"} text-white text-[11px] font-mono uppercase tracking-wider px-2 py-1`}>{s.replace(/_/g, " ")}</span>;

export default function OrderPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const { t, fmt, config } = useApp();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = params.get("key") || orderKey.get(orderId);
  const autoPay = useRef(params.get("pay") === "1");

  const load = useCallback(() => api.get(`/orders/${orderId}`, { params: { key } }).then((r) => setO(r.data)).catch(() => setO(false)), [orderId, key]);
  const sync = useCallback(async () => {
    try { await api.post(`/orders/${orderId}/sync`, null, { params: { key } }); } catch { /* ignore */ }
    load();
  }, [orderId, key, load]);

  const pay = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post(`/orders/${orderId}/pay`, null, { params: { key } });
      const snap = await loadSnap(config);
      snap.pay(r.data.token, {
        onSuccess: () => { toast.success("Payment received"); sync(); },
        onPending: () => { toast("Waiting for payment"); sync(); },
        onError: () => toast.error("Payment failed"),
        onClose: () => sync(),
      });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }, [orderId, key, config, sync]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (o && o.status === "pending" && autoPay.current && config.midtrans_client_key) { autoPay.current = false; pay(); }
  }, [o, config, pay]);

  if (o === false) return <p className="text-center py-32" data-testid="order-not-found">Order not found.</p>;
  if (!o) return <div className="h-[60vh]" />;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16 space-y-10" data-testid="order-page">
      <div className="space-y-3">
        <p className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">ORDER</p>
        <h1 className="font-serif text-4xl sm:text-5xl" data-testid="order-id">{o.order_id}</h1>
        <StatusBadge s={o.status} />
        {!["pending", "cancelled"].includes(o.status) && (
          <button onClick={() => downloadBlob(`/orders/${o.order_id}/invoice?key=${encodeURIComponent(key)}`, `invoice-${o.order_id}.pdf`)} data-testid="invoice-download-btn"
            className="ml-3 inline-flex items-center gap-2 border border-[#3B220C] px-3 py-1 text-xs hover:bg-[#F6F2EE]"><FileDown size={14} />{t("invoice")}</button>
        )}
      </div>
      {o.status === "pending" && (
        <div className="bg-[#3B220C] text-white p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div><p className="flex items-center gap-2"><Clock size={16} />Awaiting payment</p><p className="text-2xl mt-1">{fmt(o.total)}</p></div>
          <div className="flex gap-3">
            <button onClick={pay} disabled={busy} data-testid="pay-now-btn" className="bg-white text-[#3B220C] px-7 py-3 text-sm">{t("pay_now")}</button>
            <button onClick={sync} data-testid="check-payment-btn" aria-label="Refresh" className="border border-white/40 px-4"><RefreshCw size={16} /></button>
          </div>
        </div>
      )}
      {o.status === "paid" && (
        <div className="border border-[#2E6B40]/40 bg-[#2E6B40]/5 p-6 flex gap-3 text-sm" data-testid="order-paid-note">
          <CheckCircle2 className="text-[#2E6B40]" size={20} />
          <span>Thank you! {o.user_id && <>Patterns, courses and bookings are in <Link to="/account" className="underline">your account</Link>.</>}</span>
        </div>
      )}
      <div className="bg-white border border-[#E6DFD5] divide-y divide-[#E6DFD5]">
        {o.items.map((i, k) => (
          <div key={k} className="p-5 flex justify-between gap-4 text-sm">
            <div><p className="font-medium">{i.name} ×{i.qty}</p>
              <p className="text-xs text-[#6E6259]">{Object.values(i.variant || {}).join(" · ")}{i.session_start && new Date(i.session_start).toLocaleString()}</p>
              {i.preorder && <p className="text-xs text-[#8C4F27]" data-testid="order-item-preorder">{t("po_ships").replace("{n}", i.preorder_days)}</p>}</div>
            <span>{fmt(i.price * i.qty)}</span>
          </div>
        ))}
        <div className="p-5 space-y-1 text-sm">
          <div className="flex justify-between"><span>{t("subtotal")}</span><span>{fmt(o.subtotal)}</span></div>
          {o.discount > 0 && <div className="flex justify-between"><span>{t("discount")} ({o.coupon_code})</span><span>−{fmt(o.discount)}</span></div>}
          {o.member_discount > 0 && <div className="flex justify-between"><span>Member</span><span data-testid="order-member-discount">−{fmt(o.member_discount)}</span></div>}
          {o.shipping.method !== "none" && <div className="flex justify-between"><span>{o.shipping.method === "pickup" ? t("pickup") : `${o.shipping.courier_name} ${o.shipping.service_name}`}</span><span>{fmt(o.shipping.cost)}</span></div>}
          <div className="flex justify-between text-lg pt-2"><span>{t("total")}</span><span>{fmt(o.total)}</span></div>
        </div>
      </div>
      {o.tracking_number && <p className="flex items-center gap-2 text-sm" data-testid="order-tracking"><Package size={16} />Tracking: <span className="font-mono">{o.tracking_number}</span>{o.tracking_status && <span className="text-xs text-[#6E6259]">· {o.tracking_status}</span>}</p>}
      {o.tracking_history?.length > 0 && (
        <ol className="border border-[#E6DFD5] bg-white divide-y divide-[#E6DFD5] text-xs" data-testid="courier-tracking-history">
          {[...o.tracking_history].reverse().map((h, k) => <li key={k} className="p-3 flex gap-4"><span className="w-36 text-[#6E6259]">{h.at ? new Date(h.at).toLocaleString() : ""}</span><span>{h.note}</span></li>)}
        </ol>
      )}
      <ol className="border-l border-[#E6DFD5] pl-6 space-y-3 text-sm" data-testid="order-history">
        {o.history.map((h, k) => <li key={k}><span className="capitalize">{h.status.replace(/_/g, " ")}</span> <span className="text-[#6E6259]">· {new Date(h.at).toLocaleString()}</span></li>)}
      </ol>
    </div>
  );
}
